import $ from 'jquery';

import { api, format } from 'JIX/utils.js';
import getLayers from './clusteredJobsLayers.js';
import { featureBounds, getMapColors } from './utils.js';
import JobAdElementsManager from 'JIX/jobad/elements/ElementsManager.js';

const defaultSourceOptions = {
    type: 'geojson',
    cluster: true,
    clusterMaxZoom: 15, // Don't cluster when below this zoom level
    clusterRadius: 40,
    clusterProperties: {
        sum_job_count: ['+', ['get', 'job_count'] ],
    },
    generateId: true, // Needed for feature selection
};

function loadImage(map, name, url, { width, height }) {
    return new Promise((resolve, reject) => {
        const scaleFactor = Math.ceil(window.devicePixelRatio || 1);
        const img = new Image(width * scaleFactor, height * scaleFactor);
        img.onload = () => {
            map.addImage(name, img);
            resolve();
        };
        img.onerror = () => reject();
        img.src = url;
    });
}

/**
 * Show job markers on a map. The markers are automatically clustered when
 * multiple markers are close together.
 */
export default class ClusteredJobs {
    constructor(options) {
        this._source_name = 'jobsearch';
        this._selected_feature_id = null;
        this._source_options = {
            cluster: !options.disableClustering,
        };
        this._layerOptions = {
            hideJobCounts: options.hideJobCounts,
        };
    }

    /**
     * Add layers to map instance.
     * @param {mapboxgl.Map} map - A map instance.
     */
    addTo(map) {
        this.map = map;
        this.map.on('load', () => this._init());
        return this;
    }

    /**
     * Update the job data.
     * @param {FeatureCollection} data - A feature collection with jobs.
     * @param {boolean} [true] - Fit the map bounds to the new data.
     */
    setData(data, fitBounds = true) {
        if (this.map.loaded()) {
            this.map.getSource(this._source_name).setData(data);
        } else {
            this._initialData = data;
        }
        if (fitBounds) {
            this._fitToFeatures(data.features, { animate: false });
        }
    }

    selectFeature(feature) {
        if (this._selected_feature_id && this._selected_feature_id !== feature.id) {
            this.deselectFeature();
        }
        this.map.setFeatureState(
            { source: this._source_name, id: feature.id },
            { selected: true }
        );
        this._selected_feature_id = feature.id;
    }

    deselectFeature() {
        if (this._selected_feature_id) {
            this.map.setFeatureState(
                { source: this._source_name, id: this._selected_feature_id },
                { selected: false }
            );
        }
        this._selected_feature_id = null;
    }

    /**
     * Show info window for the given feature.
     * @param {Feature} feature - A job feature.
     * @param {LngLatLike} [undefined] coordinates - Move map to the given coordinates.
     */
    showInfoWindow(feature, coordinates = undefined) {
        const { type, objectId, tid } = feature.properties;

        if (!tid) {
            this.hideInfoWindow();
            return;
        }

        this.selectFeature(feature);

        if (coordinates) {
            this.map.easeTo({ center: coordinates, offset: [0, -100] });
        }

        if (this._$container && this._$container.data('object-id') === objectId) {
            // Info window for this marker already visible
            return;
        }
        this.hideInfoWindow();

        this._$container = $('<div>')
            .addClass('jix-mapboxgl-bottomsheet mod-hidden')
            .data('object-id', objectId)
            .on('click', '.close', () => {
                this.deselectFeature();
                this.hideInfoWindow();
            })
            .appendTo(this.map.getContainer());

        this._getInfoContent(type, objectId, tid)
            .then(html => {
                // Info window may have been hidden again at this point
                if (this._$container) {
                    this._$container
                        .html(html)
                        .removeClass('mod-hidden');

                    if (this._elementsManager) {
                        this._elementsManager.handleReload();
                    } else {
                        this._elementsManager = new JobAdElementsManager();
                    }
                }
            })
            .catch(err => {
                if (err.status !== 0) {
                    window.Sentry.captureException(err);
                }
            });
    }

    /**
     * Hide the info window.
     */
    hideInfoWindow() {
        if (!this._$container) {
            return;
        }
        const $container = this._$container;
        this._$container = null;
        $container.addClass('mod-hidden');
        setTimeout(() => $container.remove(), 1000);
    }

    _init() {
        const data = this._initialData || null;
        this._initialData = null;
        this.map.addSource(this._source_name, {
            ...defaultSourceOptions,
            ...this._source_options,
            data,
        });

        this._loadImages()
            .then(() => this._setupLayers())
            .catch(err => window.Sentry.captureException(err));
    }

    /**
     * Fit map bounds the the given features.
     * @param {Array} features
     */
    _fitToFeatures(features, options = {}) {
        if (features.length === 1) {
            const zoom = features[0].properties.type === 'area' ? 10 : 14;
            this.map.easeTo({ zoom, center: features[0].geometry.coordinates, ...options });
        } else if (features.length > 1) {
            const bounds = featureBounds(features);
            const padding = this._calculateFitPadding(features.length);
            this.map.fitBounds(bounds, { padding, ...options });
        }
    }

    _calculateFitPadding(numFeatures) {
        const { width, height } = this.map.getCanvas().getBoundingClientRect();
        const landscape = width > height;
        const scale = Math.max(0.2, 0.8 - (numFeatures * 0.05));
        const padding = Math.min(width, height) / 2 * scale;
        const diff = Math.abs(width - height) / 2;
        const horisontalPadding = Math.round(landscape  ? padding + diff : padding);
        const verticalPadding   = Math.round(!landscape ? padding + diff : padding);
        return {
            left: horisontalPadding,
            right: horisontalPadding,
            top: verticalPadding,
            bottom: verticalPadding,
        };
    }

    _loadImages() {
        const site = window.Stash.common.site;
        const images = [];
        images.push(loadImage(this.map, `address`, `/img/jobindex20/icons/map/map-address-marker-${site}.svg`, { width: 35, height: 35 }));
        images.push(loadImage(this.map, `address-selected`, `/img/jobindex20/icons/map/map-address-marker-selected-${site}.svg`, { width: 35, height: 35 }));
        return Promise.all(images);
    }

    _setupLayers() {
        const colors = getMapColors(this.map);
        const layers = getLayers(colors, this._layerOptions);
        for (let layer of layers) {
            this.map.addLayer(layer);
        }

        this.map.on('click', event => {
            const features = this.map.queryRenderedFeatures(event.point);
            // If clicking on a job marker don't hide the bottomsheet as it
            // will be shown again shortly.
            if (features.length && features[0].properties.job_count) {
                return;
            }
            this.deselectFeature();
            this.hideInfoWindow();
        });

        // When clicking on a cluster zoom in until the cluster expands
        this.map.on('click', 'clusters_circle', async event => {
            const feature = event.features[0];
            const clusterId = feature.properties.cluster_id;

            const features = await this.map.getSource(this._source_name)
                .getClusterLeaves(clusterId, Number.MAX_VALUE, 0);
            this._fitToFeatures(features);
        });

        // Show popup with jobs when clicking an address or area
        for (let layer of ['job_address', 'job_area_circle']) {
            this.map.on('click', layer, event => this._handleFeatureClick(event));
        }

        // Make cursor into a pointer when hovering features
        for (let layer of ['clusters_circle', 'job_address', 'job_area_circle']) {
            this.map.on('mouseenter', layer, (event) => {
                const props = event.features[0].properties;
                if (props.job_count || props.point_count) {
                    this.map.getCanvas().style.cursor = 'pointer';
                }
            });
            this.map.on('mouseleave', layer, () => {
                this.map.getCanvas().style.cursor = '';
            });
        }
    }

    _handleFeatureClick(event) {
        const feature = event.features[0];
        const coordinates = feature.geometry.coordinates.slice();

        // Ensure that if the map is zoomed out such that multiple
        // copies of the feature are visible, the popup appears
        // over the copy being pointed to.
        while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        this.showInfoWindow(feature, coordinates);
    }

    _getInfoContent(type, objectId, tid) {
        const url = format('/jobsearch/map/info-window/{type}-{objectId}/{tid}', { type, objectId, tid });
        return api('GET', url);
    }
}
